import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { switchLanguage } from "../../redux/languageSlice";
import { useNavigate } from "react-router-dom";
import styles from "./GroupCockpitProfile.module.css";
import {
  CustomTextInput,
  CustomButton,
  CustomDatePicker,
  CustomDropdown,
  CustomFileInput,
} from "../../components";
import {
  AxiosInstance,
  TokenService,
} from "../../services/Api";
import {
  fetchIncomeOptions,
  fetchOccupationOptions,
  fetchBloodGroupOptions,
} from "../../redux/Masterdata";
import defaultProfileImg from "../../assets/images/profile.png";
import BlobImage from "../../components/BlobImage/BlobImage";
import dayjs from "dayjs";
import moment from "moment/moment";
import PopupModal from "../../components/PopupModal/PopupModal";

const GroupCockpitProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = TokenService.getUser();
  const userId = user?.id || 0;
  const userType = user?.role?.slug;

  const roleId = Number(user?.role?.id);
  const { translations } = useSelector((state) => state.language);

  // Get master data from Redux store
  const {
    incomeOptions: reduxIncomeOptions,
    occupationOptions: reduxOccupationOptions,
    bloodGroupOptions: reduxBloodGroupOptions,
  } = useSelector((state) => state.masterData);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [errors, setErrors] = useState({});
  // Whether the profile API already returned an email for this user.
  const [hasApiEmail, setHasApiEmail] = useState(false);

  const [formData, setFormData] = useState({
    id: "",
    client_id: "",
    marital_status: "",
    firstName: "",
    middleName: "",
    lastName: "",
    emailAddress: "",
    fatherName: "",
    motherName: "",
    mobileNumber: "",
    bloodGroup: "",
    gender: "",
    phoneNumber: "",
    panNumber: "",
    aadharNumber: "",
    dateOfBirth: "",
    pincode: "",
    address1: "",
    address2: "",
    address3: "",
    city: "",
    state: "",
    country: "",
    language: "",
    income: "",
    anniversary: "",
    spouseName: "",
    profile_original_name: null,
    profile_upload_path: "",
  });
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  // ==================== ROLE HELPERS ====================
  const getUserType = (roleId) => {
    switch (Number(roleId)) {
      case 2:
      case 5:
        return "admin";
      case 3:
        return "staff";
      case 4:
        return "student";
      case 6:
        return "parent";
      default:
        return "";
    }
  };

  // Parent (roleId 6) whose profile API returned no email may enter/edit
  // their email; every other case keeps the email field disabled.
  const isParent = roleId === 6;
  const isEmailEditable = isParent && !hasApiEmail;

  // Field visibility based on roleId
  const showField = {
    fatherName: [2, 3, 5].includes(roleId),
    motherName: [2, 3, 5].includes(roleId),
    mobileNumber: [2, 3, 5, 6].includes(roleId),
    bloodGroup: [2, 3, 4, 5].includes(roleId),
    gender: [2, 3, 4, 5].includes(roleId),
    phoneNumber: [2, 3, 5, 6].includes(roleId),
    panNumber: [2, 3, 5, 6].includes(roleId),
    aadharNumber: [2, 3, 4, 5, 6].includes(roleId),
    pincode: [2, 3, 5, 6].includes(roleId),
    profilePhotoUpload: [2, 3, 5, 6].includes(roleId),
    income: [6].includes(roleId),
    maritalStatus: [3].includes(roleId),
    anniversary: [3].includes(roleId),
    spouseName: [3].includes(roleId),
    address: [2, 3, 5, 6].includes(roleId),
    cityStateCountry: [2, 3, 5, 6].includes(roleId),
  };

  // ==================== MEMOIZED DROPDOWN OPTIONS ====================
  const bloodGroupOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectBloodGroup || "Select Blood Group",
      },
      ...reduxBloodGroupOptions,
    ],
    [reduxBloodGroupOptions, translations],
  );

  const incomeOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectIncome || "Select Income",
      },
      ...reduxIncomeOptions,
    ],
    [reduxIncomeOptions, translations],
  );

  const genderOptions = [
    { value: "", label: translations.labels?.selectGender || "Select Gender" },
    { value: "1", label: translations.labels?.male || "Male" },
    { value: "2", label: translations.labels?.female || "Female" },
    { value: "3", label: translations.labels?.other || "Other" },
  ];

  const languageOptions = [
    {
      value: "",
      label: translations.labels?.selectLanguage || "Select Language",
    },
    { value: "English", label: "English" },
    { value: "Hindi", label: "Hindi" },
    { value: "Tamil", label: "Tamil" },
    { value: "Telugu", label: "Telugu" },
    { value: "Kannada", label: "Kannada" },
    { value: "Malayalam", label: "Malayalam" },
  ];

  const maritalStatusOptions = [
    { value: "single", label: translations.labels?.single || "Single" },
    { value: "married", label: translations.labels?.married || "Married" },
    { value: "divorced", label: translations.labels?.divorced || "Divorced" },
    { value: "widowed", label: translations.labels?.widowed || "Widowed" },
  ];

  // ==================== EFFECTS ====================
  useEffect(() => {
    fetchProfileData();
    fetchAllMasterData();
    if (showField.cityStateCountry) {
      fetchCountries();
    }
  }, []);

  // Fetch all master data from Redux
  const fetchAllMasterData = () => {
    dispatch(fetchBloodGroupOptions());
    if (showField.income) {
      dispatch(fetchIncomeOptions());
    }
    dispatch(fetchOccupationOptions());
  };

  // ==================== API CALLS ====================
  const fetchCountries = async () => {
    try {
      const response = await AxiosInstance.get("school/countries");
      if (response?.data?.status && Array.isArray(response.data.data)) {
        const countries = response.data.data.map((country) => ({
          value: country.id,
          label: country.name,
        }));
        setCountryOptions(countries);
      } else {
        setCountryOptions([]);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountryOptions([]);
    }
  };

  const fetchStates = async (countryId) => {
    if (!countryId) {
      setStateOptions([]);
      return;
    }
    try {
      setLoadingStates(true);
      const response = await AxiosInstance.post("school/states", {
        country_id: countryId,
      });
      if (response?.data?.status && Array.isArray(response.data.data)) {
        const states = response.data.data.map((state) => ({
          value: state.id,
          label: state.name,
        }));
        setStateOptions(states);
      } else {
        setStateOptions([]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      setStateOptions([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchProfileData = async () => {
    try {
      const userType = getUserType(roleId);
      setLoading(true);
      const response = await AxiosInstance.post(
        `/groupCockpit/profile/${userId}`,
        { user_type: userType },
      );
      if (response.data?.success) {
        const data = response.data.data;
        setError(null);
        setHasApiEmail(Boolean(data.email && String(data.email).trim()));

        const formDataObj = {
          id: data.id,
          client_id: data.client_id,
          marital_status: data.marital_status || "",
          firstName: data.first_name || "",
          middleName: data.middle_name || "",
          lastName: data.last_name || "",
          emailAddress: data.email || "",
          fatherName: data.father_name || "",
          spouseName: data.spouse_name || "",
          anniversary: data.anniversary || "",
          motherName: data.mother_name || "",
          mobileNumber: data.mobile_no ? String(data.mobile_no) : "",
          phoneNumber: data.land_line_no
            ? String(data.land_line_no)
            : data.phone_no
              ? String(data.phone_no)
              : "",
          bloodGroup: data.blood_group_id || "",
          gender: data.gender || "",
          panNumber: data.pan_no || "",
          aadharNumber: data.aadhar_no || "",
          dateOfBirth: data.dob || "",
          pincode: data.postal_code ? String(data.postal_code) : "",
          address1: data.address1 || "",
          address2: data.address2 || "",
          address3: data.address3 || "",
          city: data.city || "",
          state: {
            label: data.state_name,
            value: data.state_id,
          },
          country: {
            label: data.country_name,
            value: data.country_id,
          },
          language: data.language || "",
          income: data.annual_income_id || data.annual_income || "",

          anniversary: data.anniversary || "",
          spouseName: data.spouse_name || "",
          profile_original_name: data.profile_original_name || null,
          profile_upload_path: data.profile_photo || "",
        };
        setFormData(formDataObj);

        if (data.country_id) {
          fetchStates(data.country_id);
        }

        // Set profile image preview from API (raw path — BlobImage resolves it)
        if (data.profile_photo) {
          setProfileImagePreview(data.profile_photo);
        }
      } else {
        throw new Error(
          response.data?.message || "Failed to fetch profile data",
        );
      }
    } catch (err) {
      console.error("Error fetching profile data:", err);
      setError(err.message || "Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  // ==================== HANDLERS ====================
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    if (name === "panNumber") {
      updatedValue = value.toUpperCase();
    }

    setFormData((prev) => ({ ...prev, [name]: updatedValue }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDropdownChange = (name, selectedValue) => {
    const value =
      typeof selectedValue === "object" ? selectedValue.value : selectedValue;

    if (name === "country") {
      setFormData((prev) => ({ ...prev, state: "", [name]: value }));
      if (value) {
        fetchStates(value);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDateChange = (name, date) => {
    setFormData((prev) => ({
      ...prev,
      [name]: date && date !== null ? dayjs(date).toISOString() : null,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // FIXED: CustomFileInput passes file directly, NOT event.target.files
  const handleFileChange = useCallback(
    async (file) => {
      // Handle clear / no file
      if (!file) {
        setFormData((prev) => ({
          ...prev,
          profile_original_name: null,
          profile_upload_path: "",
        }));
        setProfileImagePreview(null);
        setErrors((prev) => ({ ...prev, profilePhoto: "" }));
        return;
      }

      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          profilePhoto:
            translations.labels?.onlyImageFilesAllowed ||
            "Only jpg, jpeg, png files are allowed",
        }));
        return;
      }

      try {
        // Upload file to server
        const formDataPayload = new FormData();
        formDataPayload.append("content_image", file);
        formDataPayload.append("type", "UserProfile");

        const response = await AxiosInstance.post(
          "/content/upload",
          formDataPayload,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        if (Array.isArray(response.data) && response.data.length > 0) {
          const uploadedFile = response.data[0];

          setFormData((prev) => ({
            ...prev,
            profile_original_name: uploadedFile.original_name,
            profile_upload_path: uploadedFile.upload_path,
          }));

          // Show preview (raw blob path — BlobImage resolves it)
          setProfileImagePreview(uploadedFile.upload_path);

          setErrors((prev) => ({ ...prev, profilePhoto: "" }));
        } else {
          throw new Error("Invalid upload response");
        }
      } catch (error) {
        console.error("File upload error:", error);
        setErrors((prev) => ({
          ...prev,
          profilePhoto:
            translations.labels?.fileUploadFailed ||
            "Failed to upload file. Please try again.",
        }));
      }
    },
    [translations],
  );

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, mobileNumber: value }));
  };
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, phoneNumber: value }));
  };
  const handleAadharChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 12);
    setFormData((prev) => ({ ...prev, aadharNumber: value }));
  };

  const handlePincodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFormData((prev) => ({ ...prev, pincode: value }));
  };

  // ==================== VALIDATION ====================
  const validateForm = () => {
    const newErrors = {};

    // Always required
    if (!formData.firstName.trim()) {
      newErrors.firstName =
        translations.labels?.firstNameRequired || "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName =
        translations.labels?.lastNameRequired || "Last name is required";
    }
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth =
        translations.labels?.dateOfBirthRequired || "Date of birth is required";
    }
    if (!formData.language) {
      newErrors.language =
        translations.labels?.languageRequired || "Language is required";
    }

    // Email — only validated when the field is editable (parent with no
    // email yet). Required + basic format check.
    if (isEmailEditable) {
      const email = formData.emailAddress.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email) {
        newErrors.emailAddress =
          translations.labels?.emailRequired || "Email is required";
      } else if (!emailRegex.test(email)) {
        newErrors.emailAddress =
          translations.labels?.emailInvalid || "Enter a valid email address";
      }
    }

    // Father name
    if (showField.fatherName && !formData.fatherName.trim()) {
      newErrors.fatherName =
        translations.labels?.fatherNameRequired || "Father name is required";
    }

    // Mobile number
    if (showField.mobileNumber) {
      if (!formData.mobileNumber.trim()) {
        newErrors.mobileNumber =
          translations.labels?.mobileNumberRequired ||
          "Mobile number is required";
      } else if (formData.mobileNumber.length !== 10) {
        newErrors.mobileNumber =
          translations.labels?.mobileNumberInvalid ||
          "Enter valid 10-digit mobile number";
      }
    }

    // Blood group
    if (showField.bloodGroup && !formData.bloodGroup) {
      newErrors.bloodGroup =
        translations.labels?.bloodGroupRequired || "Blood group is required";
    }

    // Gender
    if (showField.gender && !formData.gender) {
      newErrors.gender =
        translations.labels?.genderRequired || "Gender is required";
    }

    // Aadhar (optional but validate length if entered)
    if (
      showField.aadharNumber &&
      formData.aadharNumber &&
      formData.aadharNumber.length !== 12
    ) {
      newErrors.aadharNumber =
        translations.labels?.aadharNumberInvalid ||
        "Enter valid 12-digit Aadhar number";
    }

    // Pincode
    if (showField.pincode) {
      if (!formData.pincode.trim()) {
        newErrors.pincode =
          translations.labels?.pincodeRequired || "Pincode is required";
      } else if (formData.pincode.length !== 6) {
        newErrors.pincode =
          translations.labels?.pincodeInvalid || "Enter valid 6-digit pincode";
      }
    }

    // Income
    if (showField.income && !formData.income) {
      newErrors.income =
        translations.labels?.incomeRequired || "Income is required";
    }

    // Address
    if (showField.address && !formData.address1.trim()) {
      newErrors.address1 =
        translations.labels?.addressRequired || "Address is required";
    }

    // City, State, Country
    if (showField.cityStateCountry) {
      if (!formData.city.trim()) {
        newErrors.city =
          translations.labels?.cityRequired || "City is required";
      }
      if (!formData.state) {
        newErrors.state =
          translations.labels?.stateRequired || "State is required";
      }
      if (!formData.country) {
        newErrors.country =
          translations.labels?.countryRequired || "Country is required";
      }
    }

    // Marital status
    if (showField.maritalStatus && !formData.marital_status) {
      newErrors.marital_status =
        translations.labels?.maritalStatusRequired ||
        "Marital status is required";
    }

    // If married → anniversary + spouse name
    if (showField.maritalStatus && formData.marital_status === "married") {
      if (!formData.anniversary) {
        newErrors.anniversary =
          translations.labels?.anniversaryRequired || "Anniversary is required";
      }
      if (!formData.spouseName?.trim()) {
        newErrors.spouseName =
          translations.labels?.spouseNameRequired || "Spouse name is required";
      }
    }
    // PAN validation (optional but validate format if entered)
    if (showField.panNumber && formData.panNumber) {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

      if (!panRegex.test(formData.panNumber)) {
        newErrors.panNumber =
          translations.labels?.panNumberInvalid ||
          "Enter valid PAN (Ex: ABCDE1234F)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== SUBMIT ====================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        user_type: userType,
        client_id: formData.client_id,
        martial_status: formData.marital_status || null,
        first_name: formData.firstName.trim(),
        middle_name: formData.middleName.trim() || null,
        last_name: formData.lastName.trim(),
        email: formData.emailAddress.trim() || null,
        father_name: formData.fatherName.trim() || null,
        mother_name: formData.motherName.trim() || null,
        mobile_no: formData.mobileNumber || null,
        blood_group: formData.bloodGroup || null,
        gender: formData.gender || null,
        phone_no: formData.phoneNumber || null,
        pan_no: formData.panNumber || null,
        aadhar_no: formData.aadharNumber || null,
        dob: formData.dateOfBirth
          ? moment.utc(formData.dateOfBirth).format("YYYY-MM-DD")
          : null,
        postal_code: formData.pincode || null,
        address1: formData.address1.trim() || null,
        address2: formData.address2.trim() || null,
        address3: formData.address3.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state ? parseInt(formData.state.value) : null,
        country: formData.country ? parseInt(formData.country.value) : null,
        language: formData.language,
        annual_income: formData.income || null,
        anniversary: formData.anniversary
          ? moment.utc(formData.anniversary).format("YYYY-MM-DD")
          : null,
        spouse_name: formData.spouseName?.trim() || null,
        original_path: formData.profile_original_name || null,
        upload_path: formData.profile_upload_path || null,
        updated_by: userId,
      };

      const response = await AxiosInstance.put(
        `/groupCockpit/profile/${userId}`,
        payload,
      );

      if (response.data?.success || response.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            translations.labels?.profileUpdateSuccess ||
            "Profile updated successfully!",
          redirectPath: "/user/dashboard",
        });
        fetchProfileData();
        // Header listens for this and refetches the profile photo
        // immediately — no logout/login needed
        window.dispatchEvent(new Event("profilePhotoUpdated"));
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message || "Failed to update profile",
          redirectPath: "",
        });

        throw new Error(response.data?.message || "Failed to update profile");
      }
    } catch (err) {
      console.error("Error updating profile:", err);

      setModalState({
        open: true,
        type: "error",
        message:
          err.message ||
          translations.labels?.profileUpdateFailed ||
          "Failed to update profile. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    fetchProfileData();
  };

  const handleLanguageChange = (langCode) => {
    dispatch(switchLanguage(langCode));
    localStorage.setItem("appLanguage", langCode);
    window.location.reload();
  };

  // ==================== LOADING / ERROR STATES ====================
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.spinner}></div>
          <p>{translations.labels?.loading || "Loading profile..."}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorWrapper}>
          <i className="fa-solid fa-circle-exclamation"></i>
          <p>{error}</p>
          <button onClick={fetchProfileData} className={styles.retryButton}>
            <i className="fa-solid fa-rotate-right"></i>{" "}
            {translations.labels?.retry || "Retry"}
          </button>
        </div>
      </div>
    );
  }
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  // ==================== RENDER ====================
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        <div className={styles.card}>
          {/* Header */}
          <div className={styles.header}>
            <i className="fa-solid fa-table-cells"></i>
            <h2 className={styles.title}>
              {translations.labels?.profileSetup || "Profile Setup"}
            </h2>
          </div>
          {/* Form */}
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className="row">
              {/* First Name */}
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={translations.labels?.firstName || "First Name"}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder={
                    translations.labels?.firstNamePlaceholder ||
                    "E.g. First Name"
                  }
                  required
                  error={errors.firstName}
                />
              </div>

              {/* Middle Name */}
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={translations.labels?.middleName || "Middle Name"}
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleInputChange}
                  placeholder={
                    translations.labels?.middleNamePlaceholder ||
                    "E.g. Middle Name"
                  }
                />
              </div>

              {/* Last Name */}
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={translations.labels?.lastName || "Last Name"}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder={
                    translations.labels?.lastNamePlaceholder || "E.g. Last Name"
                  }
                  required
                  error={errors.lastName}
                />
              </div>

              {/* Email Address */}
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={translations.labels?.emailAddress || "Email Address"}
                  name="emailAddress"
                  type="email"
                  value={formData.emailAddress}
                  onChange={handleInputChange}
                  placeholder={
                    translations.labels?.emailPlaceholder ||
                    "E.g. email@example.com"
                  }
                  required
                  error={errors.emailAddress}
                  disabled={!isEmailEditable}
                />
              </div>

              {/* Father Name */}
              {showField.fatherName && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label={translations.labels?.fatherName || "Father Name"}
                    name="fatherName"
                    value={formData.fatherName}
                    onChange={handleInputChange}
                    placeholder={
                      translations.labels?.fatherNamePlaceholder ||
                      "E.g. Father Name"
                    }
                    required
                    error={errors.fatherName}
                  />
                </div>
              )}

              {/* Mother Name */}
              {showField.motherName && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label={translations.labels?.motherName || "Mother Name"}
                    name="motherName"
                    value={formData.motherName}
                    onChange={handleInputChange}
                    placeholder={
                      translations.labels?.motherNamePlaceholder ||
                      "E.g. Mother Name"
                    }
                  />
                </div>
              )}

              {/* Mobile Number */}
              {showField.mobileNumber && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label={translations.labels?.mobileNumber || "Mobile Number"}
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleMobileChange}
                    placeholder={
                      translations.labels?.mobileNumberPlaceholder ||
                      "E.g. 9876543210"
                    }
                    maxLength={10}
                    required
                    error={errors.mobileNumber}
                  />
                </div>
              )}

              {/* Blood Group (from Redux) */}
              {showField.bloodGroup && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomDropdown
                    label={translations.labels?.bloodGroup || "Blood Group"}
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    onChange={(value) =>
                      handleDropdownChange("bloodGroup", value)
                    }
                    options={bloodGroupOptions}
                    placeholder={
                      translations.labels?.selectBloodGroup ||
                      "Select Blood Group"
                    }
                    required
                    error={errors.bloodGroup}
                  />
                </div>
              )}

              {/* Gender */}
              {showField.gender && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomDropdown
                    label={translations.labels?.gender || "Gender"}
                    name="gender"
                    value={formData.gender}
                    onChange={(value) => handleDropdownChange("gender", value)}
                    options={genderOptions}
                    placeholder={
                      translations.labels?.selectGender || "Select Gender"
                    }
                    required
                    error={errors.gender}
                  />
                </div>
              )}

              {/* Phone Number */}
              {showField.phoneNumber && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label={translations.labels?.phoneNumber || "Phone Number"}
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    inputClassName={styles.markInput}
                    onChange={handlePhoneChange}
                    placeholder={
                      translations.labels?.phoneNumberPlaceholder ||
                      "E.g. 044 12345678"
                    }
                  />
                </div>
              )}

              {/* PAN Number */}
              {showField.panNumber && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label={translations.labels?.panNumber || "PAN Number"}
                    name="panNumber"
                    value={formData.panNumber}
                    onChange={handleInputChange}
                    placeholder={
                      translations.labels?.panNumberPlaceholder ||
                      "E.g. 1234567890"
                    }
                    maxLength={10}
                    error={errors.panNumber}
                  />
                </div>
              )}

              {/* Aadhar Number */}
              {showField.aadharNumber && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label={translations.labels?.aadharNumber || "Aadhar Number"}
                    name="aadharNumber"
                    value={formData.aadharNumber}
                    onChange={handleAadharChange}
                    placeholder={
                      translations.labels?.aadharNumberAltPlaceholder ||
                      "E.g. 123456789657"
                    }
                    maxLength={12}
                    error={errors.aadharNumber}
                  />
                </div>
              )}

              {/* Date of Birth */}
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomDatePicker
                  label={translations.labels?.dateOfBirth || "Date Of Birth"}
                  name="dateOfBirth"
                  value={
                    formData.dateOfBirth ? dayjs(formData.dateOfBirth) : null
                  }
                  onChange={(date) => handleDateChange("dateOfBirth", date)}
                  placeholder={
                    translations.labels?.dateOfBirthPlaceholder || "Select Date"
                  }
                  required
                  error={errors.dateOfBirth}
                />
              </div>

              {/* Pincode */}
              {showField.pincode && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label={translations.labels?.pincode || "Pincode"}
                    name="pincode"
                    value={formData.pincode}
                    onChange={handlePincodeChange}
                    placeholder={
                      translations.labels?.pincodePlaceholder || "E.g. 600028"
                    }
                    maxLength={6}
                    required
                    error={errors.pincode}
                  />
                </div>
              )}

              {/* Profile Photo Upload */}
              {showField.profilePhotoUpload && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomFileInput
                    label={translations.labels?.profilePhoto || "Profile Photo"}
                    name="profilePhoto"
                    value={formData.profile_original_name || null}
                    onChange={handleFileChange}
                    accept="image/*"
                    placeholder={
                      formData.profile_original_name
                        ? formData.profile_original_name
                        : translations.labels?.photoPlaceholder ||
                          "E.g. Mypic.jpg"
                    }
                    buttonText={
                      translations.labels?.chooseFile || "Choose File"
                    }
                  />
                  {errors.profilePhoto && (
                    <span className={styles.errorText}>
                      {errors.profilePhoto}
                    </span>
                  )}
                </div>
              )}

              {/* Income (from Redux) - Parent only */}
              {showField.income && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomDropdown
                    label={translations.labels?.income || "Income"}
                    name="income"
                    value={formData.income}
                    onChange={(value) => handleDropdownChange("income", value)}
                    options={incomeOptions}
                    placeholder={
                      translations.labels?.selectIncome || "Select Income"
                    }
                    required
                    error={errors.income}
                  />
                </div>
              )}
              {/* Marital Status - Staff only */}
              {showField.maritalStatus && (
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomDropdown
                    label={
                      translations.labels?.maritalStatus || "Marital Status"
                    }
                    name="marital_status"
                    value={formData.marital_status}
                    onChange={(value) =>
                      handleDropdownChange("marital_status", value)
                    }
                    options={maritalStatusOptions}
                    placeholder={
                      translations.labels?.selectMaritalStatus ||
                      "Select Marital Status"
                    }
                    required
                    error={errors.marital_status}
                  />
                </div>
              )}

              {/* Anniversary - Staff only (when married) */}
              {showField.anniversary &&
                formData.marital_status === "married" && (
                  <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                    <CustomDatePicker
                      label={translations.labels?.anniversary || "Anniversary"}
                      name="anniversary"
                      value={
                        formData.anniversary
                          ? dayjs(formData.anniversary)
                          : null
                      }
                      onChange={(date) => handleDateChange("anniversary", date)}
                      placeholder={
                        translations.labels?.dateOfBirthPlaceholder ||
                        "Select Date"
                      }
                      required
                      error={errors.anniversary}
                    />
                  </div>
                )}

              {/* Spouse Name - Staff only (when married) */}
              {showField.spouseName &&
                formData.marital_status === "married" && (
                  <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                    <CustomTextInput
                      label={translations.labels?.spouseName || "Spouse Name"}
                      name="spouseName"
                      value={formData.spouseName}
                      onChange={handleInputChange}
                      placeholder={
                        translations.labels?.spouseNamePlaceholder ||
                        "E.g. Spouse Name"
                      }
                      required
                      error={errors.spouseName}
                    />
                  </div>
                )}

              {/* Address 1, 2, 3 */}
              {showField.address && (
                <>
                  <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                    <CustomTextInput
                      label={translations.labels?.address1 || "Address 1"}
                      name="address1"
                      value={formData.address1}
                      onChange={handleInputChange}
                      placeholder={
                        translations.labels?.addressPlaceholder || "Address"
                      }
                      required
                      error={errors.address1}
                    />
                  </div>
                  <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                    <CustomTextInput
                      label={translations.labels?.address2 || "Address 2"}
                      name="address2"
                      value={formData.address2}
                      onChange={handleInputChange}
                      placeholder={
                        translations.labels?.addressPlaceholder || "Address"
                      }
                    />
                  </div>
                  <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                    <CustomTextInput
                      label={translations.labels?.address3 || "Address 3"}
                      name="address3"
                      value={formData.address3}
                      onChange={handleInputChange}
                      placeholder={
                        translations.labels?.addressPlaceholder || "Address"
                      }
                    />
                  </div>
                </>
              )}

              {/* City, State, Country */}
              {showField.cityStateCountry && (
                <>
                  <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                    <CustomTextInput
                      label={translations.labels?.city || "City"}
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder={
                        translations.labels?.cityPlaceholder || "E.g. Chennai"
                      }
                      required
                      error={errors.city}
                    />
                  </div>
                  <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                    <CustomDropdown
                      label={translations.labels?.state || "State"}
                      name="state"
                      value={formData.state.label}
                      onChange={(value) => handleDropdownChange("state", value)}
                      options={stateOptions}
                      placeholder={
                        translations.labels?.selectState || "Select State"
                      }
                      required
                      error={errors.state}
                    />
                  </div>

                  <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                    <CustomDropdown
                      label={translations.labels?.country || "Country"}
                      name="country"
                      value={formData.country.label}
                      onChange={(value) =>
                        handleDropdownChange("country", value)
                      }
                      options={countryOptions}
                      placeholder={
                        translations.labels?.selectCountry || "Select Country"
                      }
                      required
                      error={errors.country}
                    />
                  </div>
                </>
              )}

              {/* Change Language */}
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label={
                    translations.labels?.changeLanguage || "Change Language"
                  }
                  name="language"
                  value={formData.language}
                  onChange={(value) => handleDropdownChange("language", value)}
                  options={languageOptions}
                  placeholder={
                    translations.labels?.selectLanguage || "Select Language"
                  }
                  required
                  error={errors.language}
                />
              </div>

              {/* Profile Photo Preview */}
              <div className={styles.formGroupCenter}>
                <label className={styles.photoLabel}>
                  {translations.labels?.profilePhoto || "Profile Photo"}
                </label>
                <div className={styles.profilePhotoWrapper}>
                  <BlobImage
                    path={profileImagePreview}
                    fallback={defaultProfileImg}
                    alt="Profile"
                    className={styles.profilePhoto}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.buttonGroup}>
              <CustomButton
                type="submit"
                variant="contained"
                className={styles.updateButton}
                fullWidth={false}
                loading={submitting}
                loadingText={translations.labels?.updating || "Updating..."}
              >
                {translations.labels?.updateButton || "UPDATE"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.cancelButton}
                fullWidth={false}
                onClick={handleCancel}
              >
                {translations.labels?.cancel || "CANCEL"}
              </CustomButton>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default GroupCockpitProfile;
